// ✅ CSS → import statique obligatoire
import "monaco-editor/min/vs/editor/editor.main.css";
import "monaco-editor/esm/vs/base/browser/ui/codicons/codicon/codicon.css";

// Import des contributions de langages HTML/CSS/JSON AVANT l'API
import "monaco-editor/esm/vs/language/html/monaco.contribution.js";
import "monaco-editor/esm/vs/language/css/monaco.contribution.js";
import "monaco-editor/esm/vs/language/json/monaco.contribution.js";

// Import statique de Monaco complet
import * as monacoEditor from "monaco-editor";
import { HTMLHint } from 'htmlhint';
import * as csstree from 'css-tree';

export const monaco = monacoEditor;

let initialized = false;

export async function initMonaco() {
    if (initialized) return;
    initialized = true;

    (self as any).MonacoEnvironment = {
        getWorker(_moduleId: string, label: string) {
            const map: Record<string, string> = {
                editorWorkerService: "editor.worker.js",
                json: "json.worker.js",
                css: "css.worker.js",
                html: "html.worker.js",
                typescript: "ts.worker.js",
                javascript: "ts.worker.js",
            };
            const file = map[label] ?? "editor.worker.js";
            return new Worker(`/dist/monaco-workers/${file}`, { type: "module" });
        },
    };

    if (!monaco.languages.html) {
        console.error("Monaco HTML language service is not available");
        return;
    }

    // @ts-ignore
    monaco.languages.html.htmlDefaults.setModeConfiguration({
        completionItems: true,
        hovers: true,
        documentSymbols: true,
        links: true,
        foldingRanges: true,
        selectionRanges: true,
        diagnostics: true,
        documentFormattingEdits: true,
        documentRangeFormattingEdits: true,
    });

    // @ts-ignore
    monaco.languages.html.htmlDefaults.setOptions({
        validate: true,
        format: {
            tabSize: 2,
            insertSpaces: true,
            wrapLineLength: 120,
            unformatted: 'code,pre',
            contentUnformatted: 'pre',
        },
        suggest: {
            html5: true
        }
    });

    // Configuration JSON avec schémas
    // @ts-ignore
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
        validate: true,
        allowComments: false,
        schemaValidation: 'error',
        enableSchemaRequest: true,
        schemas: []
    });

    // Configuration du formatage JSON
    // @ts-ignore
    monaco.languages.json.jsonDefaults.setModeConfiguration({
        documentFormattingEdits: true,
        documentRangeFormattingEdits: true,
    });
}

/**
 * Valide le HTML et le CSS dans un modèle Monaco
 * @param model Le modèle Monaco à valider
 * @returns Les markers d'erreurs
 */
export function validateHTMLWithCSS(model: monacoEditor.editor.ITextModel): monacoEditor.editor.IMarkerData[] {
    const code = model.getValue();
    const allMarkers: monacoEditor.editor.IMarkerData[] = [];

    // 1. Validation HTML avec HTMLHint
    const htmlMessages = HTMLHint.verify(code, {
        "tagname-lowercase": true,
        "attr-lowercase": false,
        "attr-value-double-quotes": false,
        "tag-pair": true,
        "spec-char-escape": true,
        "id-unique": true,
        "src-not-empty": true,
        "attr-no-duplication": true,
    });

    allMarkers.push(...htmlMessages.map(msg => ({
        severity: msg.type === 'error' ? monaco.MarkerSeverity.Error : monaco.MarkerSeverity.Warning,
        startLineNumber: msg.line,
        startColumn: msg.col,
        endLineNumber: msg.line,
        endColumn: msg.col + 1,
        message: msg.message,
        source: 'HTMLHint'
    })));

    // 2. Extraction et validation du CSS dans les balises <style>
    const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
    let match;

    while ((match = styleRegex.exec(code)) !== null) {
        const cssContent = match[1];
        const styleTagStart = match.index;

        // Calculer le numéro de ligne de début du CSS
        const textBeforeStyle = code.substring(0, styleTagStart + match[0].indexOf(cssContent));
        const lineOffset = textBeforeStyle.split('\n').length;

        try {
            // Valider le CSS avec CSSTree (parser CSS)
            csstree.parse(cssContent, {
                filename: 'template.html',
                positions: true,
                onParseError: (error) => {
                    // Calculer la position de l'erreur
                    const cssLines = cssContent.substring(0, error.offset).split('\n');
                    const errorLine = cssLines.length;
                    const errorColumn = cssLines[cssLines.length - 1].length + 1;

                    allMarkers.push({
                        severity: monaco.MarkerSeverity.Error,
                        startLineNumber: lineOffset + errorLine - 1,
                        startColumn: errorColumn,
                        endLineNumber: lineOffset + errorLine - 1,
                        endColumn: errorColumn + 1,
                        message: error.message,
                        source: 'CSS Parser'
                    });
                }
            });
        } catch (err: any) {
            // Si erreur de parsing non capturée par onParseError
            if (err.line && err.column) {
                allMarkers.push({
                    severity: monaco.MarkerSeverity.Error,
                    startLineNumber: lineOffset + err.line - 1,
                    startColumn: err.column,
                    endLineNumber: lineOffset + err.line - 1,
                    endColumn: err.column + 1,
                    message: err.message || 'Erreur de syntaxe CSS',
                    source: 'CSS Parser'
                });
            }
        }
    }

    return allMarkers;
}

/**
 * Valide le JSON dans un modèle Monaco
 * @param model Le modèle Monaco à valider
 * @param schema Schéma JSON optionnel pour validation
 * @returns Les markers d'erreurs
 */
export function validateJSON(model: monacoEditor.editor.ITextModel, schema?: object): monacoEditor.editor.IMarkerData[] {
    const code = model.getValue();
    const markers: monacoEditor.editor.IMarkerData[] = [];

    try {
        const parsed = JSON.parse(code);

        // Si un schéma est fourni, on peut ajouter une validation plus avancée ici
        if (schema) {
            // Validation via schéma Monaco intégré
            const uri = model.uri.toString();
            // @ts-ignore
            monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
                validate: true,
                schemas: [{
                    uri: uri,
                    fileMatch: [uri],
                    schema: schema
                }]
            });
        }
    } catch (error: any) {
        // Extraire la position de l'erreur
        const match = error.message.match(/position (\d+)/);
        let position = match ? parseInt(match[1]) : 0;

        // Calculer ligne et colonne
        const textBeforeError = code.substring(0, position);
        const lines = textBeforeError.split('\n');
        const lineNumber = lines.length;
        const columnNumber = lines[lines.length - 1].length + 1;

        markers.push({
            severity: monaco.MarkerSeverity.Error,
            startLineNumber: lineNumber,
            startColumn: columnNumber,
            endLineNumber: lineNumber,
            endColumn: columnNumber + 1,
            message: error.message,
            source: 'JSON Parser'
        });
    }

    return markers;
}
